import { Component, inject, Input, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RecaptchaFormsModule, RecaptchaModule } from 'ng-recaptcha-2';
import { MessageBus } from '../../../services/message/message-bus.service';
import { BaseControlComponent } from './base-control.component';
import { LinkLabelComponent } from '../labels/link-label.component';

@Component({
  selector: 'app-recaptcha',
  imports: [
    ReactiveFormsModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    LinkLabelComponent,
  ],
  template: `
    @if (siteKey) {
      <div
        role="group"
        [attr.aria-labelledby]="id + '-label'"
        [attr.aria-describedby]="recaptchaError ? id + '-error' : null"
      >
        <span [id]="id + '-label'" class="tw-sr-only">I'm not a robot</span>
        @if (recaptchaError) {
          <span [id]="id + '-error'" class="tw-sr-only">
            Verification expired. Check the checkbox again.
          </span>
        }
        <re-captcha
          (resolved)="onResolved($event)"
          (errored)="onErrored()"
          [formControl]="control"
          [siteKey]="siteKey"
          [size]="widgetSize"
          errorMode="handled"
        ></re-captcha>
      </div>
      <div class="tw-text-sm tw-pt-1 tw-break-words">
        If you are unable to see the CAPTCHA image(s) above, please contact
        <app-link-label (click)="messageBus.publish('show-technical-support')"
          >technical support</app-link-label
        >.
      </div>
    }
  `,
  styles: `
    /* Challenge iframe is appended to document.body, so these rules cannot be encapsulated. */
    @media (max-width: 400px) {
      body > div:has(iframe[title*='recaptcha challenge']),
      body > div:has(iframe[src*='recaptcha'][src*='bframe']) {
        left: 8px !important;
        top: 8px !important;
        right: auto !important;
        overflow: visible !important;
        transform: none !important;
      }

      iframe[title*='recaptcha challenge'],
      iframe[src*='recaptcha'][src*='bframe'] {
        width: 400px !important;
        max-width: none !important;
        transform: scale(calc((100vw - 16px) / 400px)) !important;
        transform-origin: top left !important;
      }
    }
  `,
  encapsulation: ViewEncapsulation.None,
})
export class RecaptchaComponent extends BaseControlComponent {
  messageBus = inject(MessageBus);

  @Input() siteKey: string | null = null;

  /** 164px compact widget so the checkbox fits a 320px viewport. */
  protected widgetSize: ReCaptchaV2.Size =
    window.innerWidth < 400 ? 'compact' : 'normal';

  /** Whether the reCAPTCHA widget has reported an error (e.g. expired). */
  recaptchaError = false;

  onResolved(captchaResponse: string | null) {
    if (captchaResponse === null) {
      // Token expired or widget reset – show accessible error (Google shows "Verification expired" in iframe but only fires expired-callback, not error-callback)
      this.recaptchaError = true;
      this.control.setValue('');
    } else {
      this.recaptchaError = false;
      this.control.setValue(captchaResponse);
    }
  }

  onErrored() {
    this.recaptchaError = true;
  }
}
